import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Row, Col, Button, Form, Spinner } from "react-bootstrap";
import { FaPencilAlt } from "react-icons/fa";
import { format_yyyy_mm_dd_to_DATETIME_MED } from "../../util/common";
import {
  addressedAtOptions,
  thinkYourself,
  identity,
  sexOptions,
  availableStates,
  ethnicityOptions,
  raceOptions,
  memberPassOptions
} from "../../util/patientIdentity";
import ApiService from "../../ApiService";
import { SnackbarContext } from "../AlertSystem";

//@ calculate age from a given date of birth in yyyy-mm-dd format
const getAgeFromDob = dob => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const EDIT_BUTTON_CONTAINER_STYLE = {
  position: "absolute",
  top: 12,
  right: 12,
  cursor: "pointer",
  zIndex: 10
};

const EDIT_BUTTON_STYLE = {
  color: "#28a745",
  cursor: "inherit"
};

const PatientBasicDetail = ({ patientDetail }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(patientDetail || {});
  const [loading, setLoading] = useState(false);
  const [isEditing340B, setIsEditing340B] = useState(false);
  const [partner340BInput, setPartner340BInput] = useState("");
  const [partnerLoading, setPartnerLoading] = useState(false);
  const { showSnackbar } = useContext(SnackbarContext);

  useEffect(() => {
    setFormData(patientDetail || {});
    setPartner340BInput(patientDetail?.pharmacyPartner || "");
    setIsEditing340B(!patientDetail?.pharmacyPartner);
  }, [patientDetail]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("address.")) {
      const key = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [key]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value
      }));
    }
  };

  const handleSave = async () => {
    // Enhanced zip code validation: allows 5-digit or ZIP+4 format
    if (!/^\d{5}(-\d{4})?$/.test(formData?.address?.postal_code)) {
      showSnackbar({ message: "Please enter a valid Zip Code.", severity: "error" });
      return;
    }
    try {
      setLoading(true);
      const trimmedPartner340B = typeof formData?.pharmacyPartner === "string" ? formData.pharmacyPartner.trim() : "";
      const updatedData = {
        ...formData,
        name: `${formData.firstName} ${formData.lastName}`,
        preferredName: formData?.preferredName || patientDetail?.preferredName || "",
        pharmacyPartner: trimmedPartner340B
      };
      if (trimmedPartner340B && !patientDetail?.userId) {
        showSnackbar({ message: "Patient id missing. Cannot update 340B partner.", severity: "error" });
      }
      if (trimmedPartner340B && patientDetail?.userId) {
        await ApiService.updatePharmacyPartner(trimmedPartner340B, patientDetail.userId);
      }
      const response = await ApiService.updatePatientAccountDetails(updatedData);
      if (response?.data) {
        setIsEditing(false);
        window.location.reload();
      }
    } catch (error) {
      console.error("Error updating patient details", error);
    } finally {
      setLoading(false);
      setIsEditing(false);
    }
  };

  const handlePartner340BSave = async () => {
    const trimmedValue = (partner340BInput || "").trim();
    if (!trimmedValue) {
      showSnackbar({ message: "Please enter a 340B partner.", severity: "error" });
      return;
    }
    if (!patientDetail?.userId) {
      showSnackbar({ message: "Patient id missing. Cannot update 340B partner.", severity: "error" });
      return;
    }
    let wasSuccessful = false;
    try {
      setPartnerLoading(true);
      const response = await ApiService.updatePharmacyPartner(trimmedValue, patientDetail.userId);
      if (response?.data) {
        wasSuccessful = true;
        showSnackbar({ message: "340B partner saved.", severity: "success" });
        window.location.reload();
      }
    } catch (error) {
      console.error("Error updating 340B partner", error);
      showSnackbar({ message: "Failed to save 340B partner.", severity: "error" });
    } finally {
      setPartnerLoading(false);
      setIsEditing340B(wasSuccessful ? false : !patientDetail?.pharmacyPartner);
    }
  };

  const handleCancel = () => {
    setFormData(patientDetail || {});
    setIsEditing(false);
    setIsEditing340B(false);
    setPartner340BInput(patientDetail?.pharmacyPartner || "");
  };

  return (
    <div className="patientBasicDetails" style={{ position: "relative" }}>
      {/* Display Mode: Render details as read-only */}
      {!isEditing && (
        <>
          <div style={EDIT_BUTTON_CONTAINER_STYLE}>
            <Button variant="outline" onClick={() => setIsEditing(true)} style={EDIT_BUTTON_STYLE}>
              <FaPencilAlt style={{ marginRight: 4, cursor: "inherit" }} />
              Edit
            </Button>
          </div>
          <Row className="row">
            <Col className="column">
              <h6>Legal Name</h6>
              <p>{patientDetail?.name}</p>
            </Col>
            <Col className="column">
              <h6>Email</h6>
              <p>{patientDetail?.email}</p>
            </Col>
            <Col className="column">
              <h6>Phone</h6>
              <p>{patientDetail?.phoneNumber}</p>
            </Col>
            <Col className="column">
              <h6>Date of Birth</h6>
              <p>{format_yyyy_mm_dd_to_DATETIME_MED(patientDetail?.dob)}</p>
            </Col>
          </Row>
          <Row className="row">
            <Col className="column">
              <h6>Sex assigned at birth</h6>
              <p>{patientDetail?.genderAtBirth}</p>
            </Col>
            <Col className="column">
              <h6>Age</h6>
              <p>{patientDetail?.dob ? getAgeFromDob(patientDetail.dob) : ""}</p>
            </Col>
            <Col className="column">
              <h6>State</h6>
              <p>{patientDetail?.address?.region}</p>
            </Col>
            <Col className="column">
              <h6>Address</h6>
              <p>
                {patientDetail?.address?.street_address}
                {patientDetail?.address?.street_address2 ? ` - ${patientDetail?.address?.street_address2}` : ``}
              </p>
            </Col>
          </Row>
          <Row className="row">
            <Col className="column">
              <h6>City</h6>
              <p>{patientDetail?.address?.locality}</p>
            </Col>
            <Col className="column">
              <h6>Zip Code</h6>
              <p>{patientDetail?.address?.postal_code}</p>
            </Col>
            <Col className="column">
              <h6>Ethnicity</h6>
              <p>{patientDetail?.ethnicity}</p>
            </Col>
            <Col className="column">
              <h6>Sexual orientation</h6>
              <p>{patientDetail?.sexualOrientationPreference}</p>
            </Col>
          </Row>
          <Row className="row">
            <Col className="column">
              <h6>Current gender identity</h6>
              <p>{patientDetail?.currentGenderIdentity}</p>
            </Col>
            <Col className="column">
              <h6>Is pass Member</h6>
              <p>{patientDetail?.passMember ? "Yes" : "No"}</p>
            </Col>
            <Col className="column">
              <h6>Race</h6>
              <p>{patientDetail?.race}</p>
            </Col>
            <Col className="column">
              <h6>Pronouns</h6>
              <p>{patientDetail?.pronoun}</p>
            </Col>
          </Row>
          <Row className="row">
            <Col className="column">
              <h6>340B Partner</h6>
              {!isEditing340B && patientDetail?.pharmacyPartner ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <p style={{ marginBottom: 0 }}>{patientDetail.pharmacyPartner}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsEditing340B(true);
                      setPartner340BInput(patientDetail.pharmacyPartner || "");
                    }}
                    style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 8px" }}
                  >
                    <FaPencilAlt style={{ cursor: "inherit" }} />
                    Edit
                  </Button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", maxWidth: "320px" }}>
                  <Form.Control
                    type="text"
                    name="pharmacyPartner"
                    value={partner340BInput}
                    placeholder="Enter 340B partner"
                    onChange={e => setPartner340BInput(e.target.value)}
                  />
                  {partner340BInput.trim().length > 0 && (
                    <Button variant="success" size="sm" onClick={handlePartner340BSave} disabled={partnerLoading}>
                      {partnerLoading ? <Spinner size="sm" animation="border" /> : "Submit"}
                    </Button>
                  )}
                  {patientDetail?.pharmacyPartner && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setIsEditing340B(false);
                        setPartner340BInput(patientDetail?.pharmacyPartner || "");
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </>
      )}

      {/* Edit Mode: Render form controls in a two-column layout */}
      {isEditing && (
        <Form>
          {/* Row 1: Legal Name */}
          <Row className="mb-3" align="start">
            <Col md={6}>
              <Form.Group controlId="editLegalName">
                <Form.Label>Legal First Name</Form.Label>
                <Form.Control type="text" name="firstName" value={formData?.firstName || ""} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="editLegalLastName">
                <Form.Label>Legal Last Name</Form.Label>
                <Form.Control type="text" name="lastName" value={formData?.lastName || ""} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>

          {/* New Row: Email (readOnly) and Preferred Name */}
          <Row className="mb-3" align="start">
            <Col md={6}>
              <Form.Group controlId="editEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" name="email" value={formData?.email || ""} onChange={handleChange} readOnly />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="editPhone">
                <Form.Label>Phone (eg +19876543210)</Form.Label>
                <Form.Control type="text" name="phoneNumber" value={formData?.phoneNumber || ""} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>

          {/* Row 2: Phone and Date of Birth */}
          <Row className="mb-3" align="start">
            <Col md={6}>
              <Form.Group controlId="editPreferredName">
                <Form.Label>Preferred Name</Form.Label>
                <Form.Control type="text" name="preferredName" value={formData?.preferredName || ""} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="editDOB">
                <Form.Label>Date of Birth</Form.Label>
                <Form.Control type="date" name="dob" value={formData?.dob ? formData.dob.split("T")[0] : ""} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>

          {/* Row 3: Sex assigned at birth and Age */}
          <Row className="mb-3" align="start">
            <Col md={6}>
              <Form.Group controlId="editGenderAtBirth">
                <Form.Label>Sex assigned at birth</Form.Label>
                <Form.Control as="select" name="genderAtBirth" value={formData?.genderAtBirth || ""} onChange={handleChange}>
                  <option value="">Select Sex</option>
                  {sexOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="editCurrentGender">
                <Form.Label>Current Gender Identity</Form.Label>
                <Form.Control as="select" name="currentGenderIdentity" value={formData?.currentGenderIdentity || ""} onChange={handleChange}>
                  <option value="">Select</option>
                  {identity.map((option, index) => (
                    <option key={index} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          {/* Row 4: State and Street Address */}
          <Row className="mb-3" align="start">
            <Col md={6}>
              <Form.Group controlId="editStreetAddress">
                <Form.Label>Street Address</Form.Label>
                <Form.Control type="text" name="address.street_address" value={formData?.address?.street_address || ""} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="editApartment">
                <Form.Label>Apartment, House no, Suite etc.</Form.Label>
                <Form.Control type="text" name="address.street_address2" value={formData?.address?.street_address2 || ""} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>

          {/* New Row: Apartment/House/Suite */}
          <Row className="mb-3" align="start">
            <Col md={6}>
              <Form.Group controlId="editCity">
                <Form.Label>City</Form.Label>
                <Form.Control type="text" name="address.locality" value={formData?.address?.locality || ""} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="editState">
                <Form.Label>State</Form.Label>
                <Form.Control as="select" name="address.region" value={formData?.address?.region || ""} onChange={handleChange}>
                  <option value="">Select State</option>
                  {availableStates.map((state, index) => (
                    <option key={index} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          {/* Row 5: City and Zip Code */}
          <Row className="mb-3" align="start">
            <Col md={6}>
              <Form.Group controlId="editZip">
                <Form.Label>Zip Code</Form.Label>
                <Form.Control type="text" name="address.postal_code" value={formData?.address?.postal_code || ""} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="editRace">
                <Form.Label>Race</Form.Label>
                <Form.Control as="select" name="race" value={formData?.race || ""} onChange={handleChange}>
                  <option value="">Select</option>
                  {raceOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          {/* Row 6: Ethnicity and Sexual Orientation */}
          <Row className="mb-3" align="start">
            <Col md={6}>
              <Form.Group controlId="editEthnicity">
                <Form.Label>Ethnicity</Form.Label>
                <Form.Control as="select" name="ethnicity" value={formData?.ethnicity || ""} onChange={handleChange}>
                  <option value="">Select</option>
                  {ethnicityOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="editSexOrientation">
                <Form.Label>Sexual Orientation</Form.Label>
                <Form.Control
                  as="select"
                  name="sexualOrientationPreference"
                  value={formData?.sexualOrientationPreference || ""}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {thinkYourself.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          {/* Row 7: Current Gender Identity and Pass Member */}
          <Row className="mb-3" align="start">
            <Col md={6}>
              <Form.Group controlId="editPassMember">
                <Form.Label>Is pass Member</Form.Label>
                <Form.Control as="select" name="passMember" value={formData?.passMember || false} onChange={handleChange}>
                  <option value="">Select</option>
                  {memberPassOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="editPronouns">
                <Form.Label>Pronouns</Form.Label>
                <Form.Control as="select" name="pronoun" value={formData?.pronoun || ""} onChange={handleChange}>
                  <option value="">Select</option>
                  {addressedAtOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          {/* Row 8: 340B Partner */}
          <Row className="mb-3" align="start">
            <Col md={6}>
              <Form.Group controlId="edit340BPartner">
                <Form.Label>340B Partner</Form.Label>
                <Form.Control
                  type="text"
                  name="pharmacyPartner"
                  value={formData?.pharmacyPartner || ""}
                  onChange={handleChange}
                  placeholder="Enter 340B partner name"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Save/Cancel Buttons */}
          <Row className="mb-3" style={{ textAlign: "right" }}>
            <Col>
              <Button variant="secondary" onClick={handleCancel} style={{ marginRight: "10px" }} disabled={loading}>
                Cancel
              </Button>
              <Button variant="success" onClick={handleSave} disabled={loading}>
                {loading ? <Spinner size="sm" animation="grow" /> : "Save"}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </div>
  );
};

PatientBasicDetail.propTypes = {
  patientDetail: PropTypes.shape({
    userId: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    name: PropTypes.string,
    genderAtBirth: PropTypes.string,
    dob: PropTypes.string,
    address: PropTypes.shape({
      street_address: PropTypes.string,
      street_address2: PropTypes.string,
      locality: PropTypes.string,
      region: PropTypes.string,
      postal_code: PropTypes.string
    }),
    ethnicity: PropTypes.string,
    sexualOrientationPreference: PropTypes.string,
    currentGenderIdentity: PropTypes.string,
    passMember: PropTypes.bool,
    race: PropTypes.string,
    pronoun: PropTypes.string,
    preferredName: PropTypes.string,
    pharmacyPartner: PropTypes.string
  })
};

export default PatientBasicDetail;
